import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Printer, Shield, Sticker, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import VoiceInput from "@/components/VoiceInput";
import PatientIdentityBanner from "@/components/ipd/PatientIdentityBanner";
import IPDClinicalBundle from "@/components/ipd/IPDClinicalBundle";
import KiviHealthBridge from "@/components/KiviHealthBridge";
import { IPDMedications, IPDInvestigations, IPDProcedures } from "@/components/IPDPhase2";
import { useAuth } from "@/context/AuthContext";
import PaymentModeEditor from "@/components/PaymentModeEditor";

const CATEGORIES = ["Room", "Doctor Visit", "RMO", "BMW", "Surgeon", "OT", "Implant", "Medicine", "Lab", "Anesthesia", "Blood", "Other 1", "Other 2", "Other 3"];

export default function IPDDashboard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canDelete = ["admin", "super_admin"].includes(user?.role || "");
  const [ipd, setIpd] = useState(null);
  const [charge, setCharge] = useState({ category: "Room", description: "", quantity: 1, rate: 0 });
  const [adv, setAdv] = useState({ amount: 0, mode: "Cash", type: "advance" });
  const [pay, setPay] = useState({ amount: 0, mode: "Cash", type: "payment" });
  const [dischargeForm, setDischargeForm] = useState({ condition: "Improved", advice: "Continue medication", follow_up: "1 week", summary: "", nursing_notes: "", diagnoses: "", procedures_summary: "", meds_at_discharge: "", warning_signs: "" });
  const [editingCharge, setEditingCharge] = useState(""); // bill_item id
  const [editVal, setEditVal] = useState("");
  const [editReason, setEditReason] = useState("");
  const [kiviOpen, setKiviOpen] = useState(false);

  // Import Walk-in Investigations dialog state
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [walkins, setWalkins] = useState([]);
  const [walkinPicks, setWalkinPicks] = useState({});   // { walkinId: true }
  const [walkinLoading, setWalkinLoading] = useState(false);
  const [walkinForcePaid, setWalkinForcePaid] = useState(false);
  const [editingPay, setEditingPay] = useState(null);

  const load = () => api.get(`/ipd/${id}`).then((r) => setIpd(r.data));
  useEffect(() => { load(); }, [id]);
  if (!ipd) return <div>Loading...</div>;

  const canVoidPayment = user?.role === "super_admin";
  const canRestore = user?.role === "super_admin";
  const canEditPayment = ["admin", "super_admin", "billing", "accounts"].includes(user?.role);

  const editPayment = (p) => setEditingPay(p);

  const voidBillItem = async (bid) => {
    const reason = window.prompt("Reason for voiding this bill entry (audit-logged):");
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/bill-items/${bid}`, { params: { reason: reason.trim() } });
      toast.success("Bill entry voided");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };
  const voidPayment = async (pid) => {
    const reason = window.prompt("Reason for voiding this payment (audit-logged):");
    if (!reason || !reason.trim()) return;
    try {
      await api.delete(`/payments/${pid}`, { params: { reason: reason.trim() } });
      toast.success("Payment voided");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };
  const restoreBillItem = async (bid) => {
    if (!window.confirm("Restore this voided bill entry back to the bill?")) return;
    try { await api.post(`/bill-items/${bid}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };
  const restorePayment = async (pid) => {
    if (!window.confirm("Restore this voided payment?")) return;
    try { await api.post(`/payments/${pid}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const addCharge = async () => {
    await api.post("/bill-items", { ...charge, patient_id: ipd.patient_id, ipd_id: id, amount: charge.quantity * charge.rate });
    toast.success("Charge added");
    setCharge({ category: "Room", description: "", quantity: 1, rate: 0 });
    load();
  };

  const openWalkinImport = async () => {
    setWalkinOpen(true);
    setWalkinLoading(true);
    setWalkinForcePaid(false);
    try {
      const { data } = await api.get(`/walkin-investigations/by-patient/${ipd.patient_id}`);
      setWalkins(data || []);
      setWalkinPicks({});
    } catch { toast.error("Failed to load walk-ins"); }
    finally { setWalkinLoading(false); }
  };
  const importWalkins = async () => {
    const ids = Object.keys(walkinPicks).filter((k) => walkinPicks[k]);
    if (ids.length === 0) return toast.error("Select at least one walk-in visit");
    try {
      const { data } = await api.post(`/ipd/${id}/import-walkin`, { walkin_opd_ids: ids, force_paid: walkinForcePaid });
      const skipped = (data.skipped_paid || []).length;
      const msg = skipped > 0
        ? `Imported ${data.imported_items} charge(s). Skipped ${skipped} already-paid walk-in(s) — tick 'Include already-paid' to override.`
        : `Imported ${data.imported_items} charge(s) from walk-in`;
      toast.success(msg);
      setWalkinOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  const startEditCharge = (c) => { setEditingCharge(c.id); setEditVal(String(c.amount || 0)); setEditReason(""); };
  const cancelEditCharge = () => { setEditingCharge(""); setEditVal(""); setEditReason(""); };
  const saveEditCharge = async () => {
    const amt = parseFloat(editVal);
    if (!Number.isFinite(amt) || amt < 0) return toast.error("Enter a valid amount");
    try {
      await api.put(`/bill-items/${editingCharge}/amount`, { new_amount: amt, reason: editReason.trim() || "Price override" });
      toast.success("Amount updated");
      cancelEditCharge();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  const addAdv = async () => {
    await api.post("/payments", { ...adv, patient_id: ipd.patient_id, ipd_id: id });
    toast.success("Advance recorded"); setAdv({ amount: 0, mode: "Cash", type: "advance" }); load();
  };
  const addPay = async () => {
    await api.post("/payments", { ...pay, patient_id: ipd.patient_id, ipd_id: id });
    toast.success("Payment received"); setPay({ amount: 0, mode: "Cash", type: "payment" }); load();
  };
  const discharge = async () => {
    if (!dischargeForm.summary || dischargeForm.summary.trim().length < 3) {
      if (!window.confirm("Discharge without a written summary?")) return;
    }
    await api.post("/discharge", { ipd_id: id, ...dischargeForm });
    toast.success("Discharged"); load();
    window.open(`/discharge/${id}/print`, "_blank");
  };

  const t = ipd.totals;

  return (
    <div className="space-y-4">
      {/* NABH §2 — Patient identification banner + allergy alert */}
      <PatientIdentityBanner ipd={ipd}/>

      <div className="bg-blue-50 border border-blue-200 p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-xl font-semibold">{ipd.patient_name}</h1>
              <Badge className="rounded-sm">{ipd.ipd_no}</Badge>
              {["admin", "super_admin"].includes(user?.role) && (
                <button
                  data-testid="ipd-edit-no-btn"
                  title="Edit IPD No (admin only)"
                  onClick={async () => {
                    const next = window.prompt(
                      `Change IPD No for ${ipd.patient_name}.\n\nCurrent: ${ipd.ipd_no}\n\nType the new IPD No (must be unique).`,
                      ipd.ipd_no || ""
                    );
                    if (next === null) return;
                    const t = next.trim();
                    if (!t || t === ipd.ipd_no) return;
                    try {
                      await api.put(`/ipd/${id}/ipd-no`, { ipd_no: t });
                      toast.success(`IPD No updated → ${t}`);
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Update failed");
                    }
                  }}
                  className="p-1 text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-sm">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
              <Badge variant="outline" className="rounded-sm">Bed {ipd.bed_no || "-"} · {ipd.ward || "-"}</Badge>
              {ipd.pmjay && <Badge className="bg-teal-100 text-teal-800 rounded-sm">PMJAY</Badge>}
              {ipd.admission_type && <Badge className="rounded-sm bg-slate-200 text-slate-800">{ipd.admission_type}</Badge>}
            </div>
            <div className="mt-2 text-xs text-slate-600 flex flex-wrap gap-x-4 items-center">
              <span className="flex items-center gap-1">UHID: <span className="font-mono">{ipd.uhid}</span>
                {["admin", "super_admin"].includes(user?.role) && (
                  <button
                    data-testid="ipd-edit-uhid-btn"
                    title="Edit patient UHID (admin only · propagates everywhere)"
                    onClick={async () => {
                      const next = window.prompt(
                        `Change UHID for ${ipd.patient_name}.\n\nCurrent: ${ipd.uhid}\n\nType the new UHID (must be unique — this change propagates to all OPD/IPD/Bill records).`,
                        ipd.uhid || ""
                      );
                      if (next === null) return;
                      const t = next.trim().toUpperCase();
                      if (!t || t === ipd.uhid) return;
                      try {
                        // Need full patient object for PUT /patients/{id}
                        const pr = await api.get(`/patients/${ipd.patient_id}`);
                        await api.put(`/patients/${ipd.patient_id}`, { ...pr.data, uhid: t });
                        toast.success(`UHID updated → ${t}`);
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Update failed");
                      }
                    }}
                    className="p-0.5 text-blue-700 hover:bg-blue-50 border border-blue-200 rounded-sm ml-1">
                    <Pencil className="w-2.5 h-2.5" />
                  </button>
                )}
              </span>
              <span>Doctor: <b>{ipd.doctor_name}</b></span>
              <span>Admitted: {fmtDT(ipd.admission_date)}</span>
              <span>Dx: {ipd.diagnosis || ipd.provisional_diagnosis || "-"}</span>
              {ipd.attendant_name && <span>Attendant: <b>{ipd.attendant_name}</b> ({ipd.attendant_relation || "-"}, {ipd.attendant_mobile || "-"})</span>}
              {ipd.tpa_insurer && <span>TPA: <b>{ipd.tpa_insurer}</b></span>}
            </div>
          </div>
          {ipd.status !== "discharged" && <Button data-testid="discharge-btn" onClick={discharge} className="bg-red-700 hover:bg-red-800 rounded-sm"><Printer className="w-4 h-4 mr-1"/> Discharge</Button>}
          {ipd.status === "discharged" && <Button variant="outline" onClick={() => window.open(`/discharge/${id}/print`, "_blank")}><Printer className="w-4 h-4 mr-1"/> Print Summary</Button>}
          <Button
            variant="outline"
            onClick={() => window.open(`/ipd/${id}/stickers`, "_blank")}
            className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-50"
            data-testid="ipd-print-stickers"
          >
            <Sticker className="w-4 h-4 mr-1"/>{ipd.status === "discharged" ? "Reprint Stickers" : "Print IPD Stickers"}
          </Button>
          <Button variant="outline" onClick={() => setKiviOpen(true)} className="rounded-sm" data-testid="ipd-kivi-btn"><Shield className="w-4 h-4 mr-1"/>{ipd.abha_number ? "ABHA Linked" : "KiviHealth ABHA"}</Button>
          {canDelete && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm(`Delete IPD ${ipd.ipd_no} — ${ipd.patient_name}?\n\nThis removes:\n • Bill items & payments\n • Assessments, vitals, progress notes, allergies, meds\n • Investigations, procedures\n • Frees the bed\n\nThe patient master record is kept. This cannot be undone.`)) return;
                try {
                  await api.delete(`/ipd/${id}`);
                  toast.success(`${ipd.ipd_no} deleted`);
                  nav("/ipd");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Delete failed");
                }
              }}
              className="rounded-sm border-red-400 text-red-700 hover:bg-red-50"
              data-testid="ipd-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1"/> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3">
        <div className="bg-white border p-3"><div className="label-tiny">Billed</div><div className="font-mono text-lg mt-1">{fmtINR(t.billed)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Paid</div><div className="font-mono text-lg mt-1">{fmtINR(t.paid)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Advance</div><div className="font-mono text-lg mt-1 text-teal-700">{fmtINR(t.advance)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Refund</div><div className="font-mono text-lg mt-1">{fmtINR(t.refund)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Balance</div><div className="font-mono text-lg mt-1 text-red-700">{fmtINR(t.balance)}</div></div>
      </div>

      <Tabs defaultValue="billing">
        <TabsList className="bg-white border">
          <TabsTrigger value="billing" data-testid="ipd-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="advance" data-testid="ipd-tab-advance">Advance</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="clinical" data-testid="ipd-tab-clinical">Clinical (NABH)</TabsTrigger>
          <TabsTrigger value="medications" data-testid="ipd-tab-medications">Medications</TabsTrigger>
          <TabsTrigger value="investigations" data-testid="ipd-tab-investigations">Investigations</TabsTrigger>
          <TabsTrigger value="procedures" data-testid="ipd-tab-procedures">Procedures</TabsTrigger>
          <TabsTrigger value="notes" data-testid="ipd-tab-notes">Discharge Notes</TabsTrigger>
        </TabsList>
        <TabsContent value="billing">
          <div className="bg-white border p-4">
            <div className="grid grid-cols-6 gap-2 mb-3">
              <select className="border border-slate-300 h-9 col-span-1" value={charge.category} onChange={(e) => setCharge({ ...charge, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <Input className="col-span-2 h-9" placeholder="Description" value={charge.description} onChange={(e) => setCharge({ ...charge, description: e.target.value })} />
              <Input type="number" className="h-9" placeholder="Qty" value={charge.quantity} onChange={(e) => setCharge({ ...charge, quantity: parseFloat(e.target.value) || 0 })} />
              <Input type="number" className="h-9" placeholder="Rate" value={charge.rate} onChange={(e) => setCharge({ ...charge, rate: parseFloat(e.target.value) || 0 })} />
              <Button onClick={addCharge} data-testid="add-charge" className="bg-blue-700 hover:bg-blue-800 rounded-sm">+ Add Charge</Button>
            </div>
            <div className="mb-3">
              <Button
                variant="outline"
                onClick={openWalkinImport}
                data-testid="ipd-import-walkin"
                className="rounded-sm border-teal-400 text-teal-700 hover:bg-teal-50 h-8 text-xs"
              >
                + Import Walk-in Investigations
              </Button>
              <span className="text-[10px] text-slate-500 ml-2">Pull outstanding X-ray / Lab / ECG walk-in bills into this IPD final bill</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="p-2 label-tiny">Category</th><th className="label-tiny">Description</th><th className="label-tiny">Qty</th><th className="label-tiny">Rate</th><th className="text-right label-tiny">Amount</th><th className="text-center label-tiny w-36">Actions</th></tr></thead>
              <tbody>
                {ipd.charges.map((c) => {
                  const editing = editingCharge === c.id;
                  const voided = !!c.is_void;
                  return (
                  <tr key={c.id} className={`border-b ${voided ? "bg-red-50/40 text-slate-400" : ""}`}>
                    <td className={`p-2 ${voided ? "line-through" : ""}`}>{c.category}</td>
                    <td className={voided ? "line-through" : ""}>
                      {c.description}
                      {voided && <div className="text-[10px] not-italic text-red-700 mt-0.5" data-testid={`ipd-voided-${c.id}`}>VOIDED · {c.void_reason} · by {c.voided_by_name || "—"}</div>}
                    </td>
                    <td className={voided ? "line-through" : ""}>{c.quantity}</td>
                    <td className={`font-mono ${voided ? "line-through" : ""}`}>{fmtINR(c.rate)}</td>
                    <td className={`text-right font-mono ${voided ? "line-through" : ""}`}>
                      {editing ? (
                        <div className="flex items-center justify-end gap-1">
                          <Input type="number" value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-7 w-24 text-xs" data-testid={`ipd-edit-amt-${c.id}`} autoFocus/>
                          <Input value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Reason" className="h-7 w-28 text-xs" data-testid={`ipd-edit-reason-${c.id}`}/>
                        </div>
                      ) : fmtINR(c.amount)}
                    </td>
                    <td className="text-center">
                      {voided ? (
                        canRestore ? (
                          <button onClick={() => restoreBillItem(c.id)} className="text-teal-700 hover:bg-teal-50 text-[11px] px-2 py-0.5 border border-teal-300 rounded-sm" data-testid={`ipd-restore-bill-${c.id}`}>Restore</button>
                        ) : <span className="text-[10px] text-slate-400">voided</span>
                      ) : editing ? (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={saveEditCharge} className="text-teal-700 hover:bg-teal-50 p-1 rounded-sm" data-testid={`ipd-edit-save-${c.id}`}>Save</button>
                          <button onClick={cancelEditCharge} className="text-slate-500 hover:bg-slate-100 p-1 rounded-sm">Cancel</button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => startEditCharge(c)} className="text-blue-700 hover:bg-blue-50 text-xs px-2 py-0.5 border border-blue-300 rounded-sm" data-testid={`ipd-edit-charge-${c.id}`}>Edit ₹</button>
                          {canDelete && (
                            <button onClick={() => voidBillItem(c.id)} className="text-red-700 hover:bg-red-50 text-xs px-2 py-0.5 border border-red-300 rounded-sm" data-testid={`ipd-void-charge-${c.id}`}>Void</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
                <tr className="font-semibold bg-slate-50"><td colSpan="4" className="p-2 text-right">Total Billed</td><td className="text-right font-mono">{fmtINR(t.billed)}</td><td></td></tr>
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="advance">
          <div className="bg-white border p-4 space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <Input type="number" className="h-9" placeholder="Advance Amount" value={adv.amount || ""} onChange={(e) => setAdv({ ...adv, amount: parseFloat(e.target.value) || 0 })} />
              <select className="border h-9" value={adv.mode} onChange={(e) => setAdv({ ...adv, mode: e.target.value })}>
                {["Cash", "GPay", "UPI", "Card", "Bank"].map((m) => <option key={m}>{m}</option>)}
              </select>
              <Button onClick={addAdv} data-testid="add-advance" className="bg-teal-700 hover:bg-teal-800 rounded-sm">+ Record Advance</Button>
            </div>
            <div className="text-sm space-y-1">
              {ipd.payments.filter((p) => p.type === "advance").map((p) => (
                <div key={p.id} className={`flex justify-between border-b pb-1 ${p.is_void ? "text-slate-400" : ""}`}>
                  <span className={p.is_void ? "line-through" : ""}>{p.receipt_no} · {p.mode}</span>
                  <span className="flex items-center gap-2">
                    <span className={`font-mono ${p.is_void ? "line-through" : ""}`}>{fmtINR(p.amount)}</span>
                    {p.is_void ? (
                      <>
                        <span className="text-[10px] text-red-700">VOID · {p.void_reason}</span>
                        {canRestore && <button onClick={() => restorePayment(p.id)} className="text-[10px] text-teal-700 border border-teal-300 rounded-sm px-1.5 py-0.5 hover:bg-teal-50" data-testid={`ipd-restore-adv-${p.id}`}>Restore</button>}
                      </>
                    ) : (
                      <>
                        {canEditPayment && (
                          <button onClick={() => editPayment(p)} className="text-[10px] text-blue-700 border border-blue-300 rounded-sm px-1.5 py-0.5 hover:bg-blue-50" data-testid={`ipd-edit-adv-${p.id}`}>Edit</button>
                        )}
                        {canVoidPayment && (
                          <button onClick={() => voidPayment(p.id)} className="text-[10px] text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5 hover:bg-red-50" data-testid={`ipd-void-adv-${p.id}`}>Void</button>
                        )}
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="payments">
          <div className="bg-white border p-4 space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <Input type="number" className="h-9" placeholder="Payment Amount" value={pay.amount || ""} onChange={(e) => setPay({ ...pay, amount: parseFloat(e.target.value) || 0 })} />
              <select className="border h-9" value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
                {["Cash", "GPay", "UPI", "Card", "Bank"].map((m) => <option key={m}>{m}</option>)}
              </select>
              <Button onClick={addPay} data-testid="add-payment" className="bg-blue-700 hover:bg-blue-800 rounded-sm">+ Record Payment</Button>
            </div>
            <div className="text-sm space-y-1">
              {ipd.payments.filter((p) => p.type === "payment").map((p) => (
                <div key={p.id} className={`flex justify-between border-b pb-1 ${p.is_void ? "text-slate-400" : ""}`}>
                  <span className={p.is_void ? "line-through" : ""}>{p.receipt_no} · {p.mode}</span>
                  <span className="flex items-center gap-2">
                    <span className={`font-mono ${p.is_void ? "line-through" : ""}`}>{fmtINR(p.amount)}</span>
                    {p.is_void ? (
                      <>
                        <span className="text-[10px] text-red-700">VOID · {p.void_reason}</span>
                        {canRestore && <button onClick={() => restorePayment(p.id)} className="text-[10px] text-teal-700 border border-teal-300 rounded-sm px-1.5 py-0.5 hover:bg-teal-50" data-testid={`ipd-restore-pay-${p.id}`}>Restore</button>}
                      </>
                    ) : (
                      <>
                        {canEditPayment && (
                          <button onClick={() => editPayment(p)} className="text-[10px] text-blue-700 border border-blue-300 rounded-sm px-1.5 py-0.5 hover:bg-blue-50" data-testid={`ipd-edit-pay-${p.id}`}>Edit</button>
                        )}
                        {canVoidPayment && (
                          <button onClick={() => voidPayment(p.id)} className="text-[10px] text-red-700 border border-red-300 rounded-sm px-1.5 py-0.5 hover:bg-red-50" data-testid={`ipd-void-pay-${p.id}`}>Void</button>
                        )}
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="clinical">
          <IPDClinicalBundle ipd={ipd} onChange={load}/>
        </TabsContent>

        <TabsContent value="medications">
          <IPDMedications ipdId={id}/>
        </TabsContent>

        <TabsContent value="investigations">
          <IPDInvestigations ipdId={id}/>
        </TabsContent>

        <TabsContent value="procedures">
          <IPDProcedures ipdId={id}/>
        </TabsContent>

        <TabsContent value="notes">
          <div className="bg-white border p-4 space-y-3">
            <div className="label-tiny">Discharge Summary — NABH structured</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Final Diagnoses</label>
                <VoiceInput testid="ipd-diagnoses" rows={2}
                  value={dischargeForm.diagnoses}
                  onChange={(v) => setDischargeForm({ ...dischargeForm, diagnoses: v })}
                  placeholder="Primary + comorbidities (ICD-10 friendly)…"/>
              </div>
              <div>
                <label className="text-xs text-slate-600">Procedures / Surgery Performed</label>
                <VoiceInput testid="ipd-procedures-sum" rows={2}
                  value={dischargeForm.procedures_summary}
                  onChange={(v) => setDischargeForm({ ...dischargeForm, procedures_summary: v })}
                  placeholder="Summary from Procedures tab (auto-fill soon)…"/>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-600">Nursing Notes</label>
              <VoiceInput testid="ipd-nursing-notes" rows={2}
                value={dischargeForm.nursing_notes}
                onChange={(v) => setDischargeForm({ ...dischargeForm, nursing_notes: v })}
                placeholder="Vitals trend, medication compliance, observations…"/>
            </div>
            <div>
              <label className="text-xs text-slate-600">Course in Hospital</label>
              <VoiceInput testid="ipd-discharge-summary" rows={4}
                value={dischargeForm.summary}
                onChange={(v) => setDischargeForm({ ...dischargeForm, summary: v })}
                placeholder="Day-wise course, response to treatment, complications, condition at discharge…"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Medications at Discharge</label>
                <VoiceInput testid="ipd-meds-discharge" rows={3}
                  value={dischargeForm.meds_at_discharge}
                  onChange={(v) => setDischargeForm({ ...dischargeForm, meds_at_discharge: v })}
                  placeholder="Rx to continue after discharge…"/>
              </div>
              <div>
                <label className="text-xs text-slate-600">Warning Signs / When to Return</label>
                <VoiceInput testid="ipd-warning-signs" rows={3}
                  value={dischargeForm.warning_signs}
                  onChange={(v) => setDischargeForm({ ...dischargeForm, warning_signs: v })}
                  placeholder="Fever &gt; 101, bleeding, sudden pain, breathlessness…"/>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600">Condition at Discharge</label>
                <Input value={dischargeForm.condition}
                  onChange={(e) => setDischargeForm({ ...dischargeForm, condition: e.target.value })}
                  data-testid="ipd-condition"/>
              </div>
              <div>
                <label className="text-xs text-slate-600">Follow-up</label>
                <Input value={dischargeForm.follow_up}
                  onChange={(e) => setDischargeForm({ ...dischargeForm, follow_up: e.target.value })}
                  placeholder="1 week / 15 days" data-testid="ipd-followup"/>
              </div>
              <div className="col-span-1">
                <label className="text-xs text-slate-600">Discharge Advice</label>
                <VoiceInput as="input" testid="ipd-advice"
                  value={dischargeForm.advice}
                  onChange={(v) => setDischargeForm({ ...dischargeForm, advice: v })}
                  placeholder="Continue medication, salt-restricted diet…"/>
              </div>
            </div>
            <div className="text-[11px] text-slate-500">
              All fields are attached to the printed Discharge Summary when you click <b>Discharge</b> above.
              Voice dictation supports English &amp; Gujarati — pick language via the pill inside the mic button.
            </div>
          </div>
        </TabsContent>
      </Tabs>
      <KiviHealthBridge
        open={kiviOpen}
        onOpenChange={setKiviOpen}
        patient={{
          id: ipd.patient_id, uhid: ipd.uhid, name: ipd.patient_name,
          age: ipd.age, gender: ipd.gender, mobile: ipd.mobile,
          address: ipd.address, city: ipd.city, state: ipd.state,
          pincode: ipd.pincode, blood_group: ipd.blood_group,
          abha_number: ipd.abha_number, abha_address: ipd.abha_address,
        }}
        onLinked={() => { setKiviOpen(false); load(); }}
      />

      {/* Import Walk-in Investigations dialog */}
      <Dialog open={walkinOpen} onOpenChange={setWalkinOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import Walk-in Investigations · {ipd.patient_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {walkinLoading && <div className="text-sm text-slate-500 p-4 text-center">Loading walk-ins…</div>}
            {!walkinLoading && walkins.length === 0 && (
              <div className="text-sm text-slate-500 p-4 text-center border rounded-sm">No walk-in investigation visits found for this patient.</div>
            )}
            {!walkinLoading && walkins.length > 0 && (
              <table className="w-full text-xs" data-testid="ipd-walkin-import-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left">
                    <th className="p-2 w-8"></th>
                    <th className="p-2">Date</th>
                    <th className="p-2">OPD No</th>
                    <th className="p-2">Tests</th>
                    <th className="p-2">Payment</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {walkins.map((w) => {
                    const already = !!w.imported_to_ipd_id;
                    const checked = !!walkinPicks[w.id];
                    return (
                      <tr key={w.id} className={`border-b ${already ? "bg-slate-50 opacity-70" : "hover:bg-blue-50"}`} data-testid={`ipd-walkin-import-row-${w.id}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            disabled={already}
                            checked={checked}
                            onChange={(e) => setWalkinPicks((prev) => ({ ...prev, [w.id]: e.target.checked }))}
                            data-testid={`ipd-walkin-pick-${w.id}`}
                          />
                        </td>
                        <td className="p-2 text-[11px]">{(w.visit_date || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="p-2 font-mono text-[11px]">{w.opd_no}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {(w.investigations || []).slice(0, 3).map((n, i) => (
                              <Badge key={`${w.id}-imp-${i}`} variant="outline" className="text-[10px]">{n}</Badge>
                            ))}
                            {(w.investigations || []).length > 3 && <span className="text-[10px] text-slate-400">+{w.investigations.length - 3}</span>}
                          </div>
                        </td>
                        <td className="p-2">
                          <Badge variant="outline" className={`text-[10px] ${
                            w.payment_status === "PAID" ? "border-teal-400 text-teal-700" :
                            w.payment_status === "SCHEME" ? "border-purple-400 text-purple-700" :
                            w.payment_status === "PARTIAL" ? "border-amber-400 text-amber-700" :
                            "border-red-400 text-red-700"
                          }`}>{w.payment_mode} · {w.payment_status}</Badge>
                          {already && <div className="text-[10px] text-blue-700 mt-0.5">Already in an IPD bill</div>}
                        </td>
                        <td className="p-2 text-right font-mono">{fmtINR(w.total_amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <label className="flex items-center gap-1.5 text-xs text-slate-600 mr-auto" data-testid="ipd-walkin-force-paid-lbl">
              <input
                type="checkbox"
                checked={walkinForcePaid}
                onChange={(e) => setWalkinForcePaid(e.target.checked)}
                data-testid="ipd-walkin-force-paid"
              />
              Include already-paid (CASH / UPI / CARD) — <span className="text-amber-700">refund walk-in payment separately</span>
            </label>
            <Button variant="outline" onClick={() => setWalkinOpen(false)}>Cancel</Button>
            <Button
              onClick={importWalkins}
              className="bg-teal-700 hover:bg-teal-800 rounded-sm"
              data-testid="ipd-walkin-import-confirm"
              disabled={Object.values(walkinPicks).every((v) => !v)}
            >
              Import Selected → Add to Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editingPay && (
        <PaymentModeEditor
          payment={editingPay}
          onClose={() => setEditingPay(null)}
          onSaved={() => { setEditingPay(null); load(); }}
        />
      )}
    </div>
  );
}
